import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { useGallery } from "../context/GalleryContext";
import { useLightbox } from "../hooks/useLightbox";
import SectionLabel from "../components/SectionLabel";
import ArtworkCard from "../components/ArtworkCard";
import ArtworkLightbox from "../components/ArtworkLightbox";
import { tokens } from "../theme";

function FilterPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      sx={{
        cursor: "pointer",
        px: 2,
        py: 0.8,
        fontSize: "0.72rem",
        fontWeight: 600,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: active ? tokens.bone : tokens.taupe,
        bgcolor: active ? tokens.ink : "transparent",
        border: `1px solid ${active ? tokens.ink : tokens.line}`,
        transition: "all 160ms ease",
        "&:hover": { borderColor: tokens.ink, color: active ? tokens.bone : tokens.ink },
      }}
    >
      {children}
    </Box>
  );
}

export default function Works() {
  const { artworks, collections } = useGallery();
  const [filter, setFilter] = useState<string>("all");

  const list = useMemo(() => {
    const base = [...artworks].sort((a, b) => Number(b.featured) - Number(a.featured) || a.createdAt - b.createdAt);
    return filter === "all" ? base : base.filter((a) => a.collectionId === filter);
  }, [artworks, filter]);

  const lb = useLightbox(list);

  return (
    <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 8 } }}>
      <SectionLabel>The collection</SectionLabel>
      <Box sx={{ mt: 2, display: "flex", alignItems: "baseline", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h1" sx={{ fontSize: { xs: "2.8rem", md: "4rem" } }}>
          Works
        </Typography>
        <Typography sx={{ fontFamily: tokens.display, fontStyle: "italic", fontSize: "1.3rem", color: tokens.taupe }}>
          {list.length} {list.length === 1 ? "piece" : "pieces"}
        </Typography>
      </Box>

      <Box sx={{ mt: 4, mb: 5, display: "flex", gap: 1.2, flexWrap: "wrap" }}>
        <FilterPill active={filter === "all"} onClick={() => setFilter("all")}>All</FilterPill>
        {collections.map((c) => (
          <FilterPill key={c.id} active={filter === c.id} onClick={() => setFilter(c.id)}>
            {c.name}
          </FilterPill>
        ))}
      </Box>

      {list.length === 0 ? (
        <Typography sx={{ py: 10, textAlign: "center" }}>No pieces here yet.</Typography>
      ) : (
        <Box sx={{ columnGap: { xs: 2.5, md: 4 }, columnCount: { xs: 1, sm: 2, md: 3 } }}>
          {list.map((a) => (
            <Box key={a.id} sx={{ breakInside: "avoid", mb: { xs: 4, md: 5 } }}>
              <ArtworkCard art={a} onOpen={lb.open} />
            </Box>
          ))}
        </Box>
      )}

      <ArtworkLightbox art={lb.active} collection={collections.find((c) => c.id === lb.active?.collectionId)} onClose={lb.close} onPrev={lb.prev} onNext={lb.next} />
    </Container>
  );
}
